/**
 * SettingsVendorsPage — Landlord Portal
 *
 * Route: /settings/vendors
 *
 * Vendor CRUD: list, create, edit trades/contact info, deactivate.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  listVendors,
  createVendor,
  updateVendor,
  deactivateVendor,
  type Vendor,
  type CreateVendorBody,
  type UpdateVendorBody,
} from '../api/vendors.api';
import { PageHeader } from '../components/layout/index';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import { TradesCheckboxGroup } from '../components/vendors/TradesCheckboxGroup';
import { CounterpartyAddressesPanel } from '../components/CounterpartyAddressesPanel';

const CATEGORY_CLASS: Record<string, string> = {
  plumbing: 'bg-info/15 text-info',
  electrical: 'bg-warning/15 text-warning',
  hvac: 'bg-primary/15 text-primary',
  appliance: 'bg-secondary text-secondary-foreground',
  structural: 'bg-destructive/15 text-destructive',
  general: 'bg-muted text-foreground',
};

interface VendorFormState {
  name: string;
  trades: string[];
  phone: string;
  email: string;
  notes: string;
}

const emptyForm = (): VendorFormState => ({
  name: '',
  trades: [],
  phone: '',
  email: '',
  notes: '',
});

function fromVendor(v: Vendor): VendorFormState {
  return {
    name: v.name,
    trades: v.trades,
    phone: v.phone ?? '',
    email: v.email ?? '',
    notes: v.notes ?? '',
  };
}

function VendorModal({
  title,
  form,
  onFormChange,
  onSubmit,
  onCancel,
  submitting,
  error,
  triggerRef,
  children,
}: {
  title: string;
  form: VendorFormState;
  onFormChange: (f: VendorFormState) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitting: boolean;
  error: string | null;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  /** Extra content rendered below the form fields (e.g. the recognized-addresses panel in edit mode). */
  children?: React.ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Run once on mount: set inert on main and focus first element
  useEffect(() => {
    const main = document.querySelector('main');
    main?.setAttribute('inert', '');
    const dialog = dialogRef.current;
    if (dialog) {
      const firstFocusable = dialog.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }
    return () => {
      document.querySelector('main')?.removeAttribute('inert');
    };
  }, []);

  // Re-subscribe keyboard handler when callbacks change (does not re-focus)
  useEffect(() => {
    const dialog = dialogRef.current;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // A confirmation opened from inside this modal (e.g. removing a recognized address) owns Escape.
        if (document.querySelectorAll('[aria-modal="true"]').length > 1) return;
        onCancel();
        triggerRef?.current?.focus();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        dialog?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCancel, triggerRef]);

  function handleCancel() {
    onCancel();
    triggerRef?.current?.focus();
  }

  return createPortal(
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vendor-modal-title"
      data-testid="vendor-modal"
    >
      <div className="bg-card rounded-lg border border-border p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h3 id="vendor-modal-title" className="text-lg font-semibold text-foreground m-0 mb-4">
          {title}
        </h3>

        <div className="flex flex-col gap-3">
          <div>
            <label htmlFor="vendor-name" className="block text-sm font-medium text-foreground mb-1">
              Name <span aria-hidden="true">*</span>
            </label>
            <input
              id="vendor-name"
              data-testid="vendor-form-name"
              type="text"
              value={form.name}
              onChange={(e) => onFormChange({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              aria-label="Vendor name"
              aria-required="true"
              placeholder="Company or person name"
            />
          </div>

          <div>
            <span className="block text-sm font-medium text-foreground mb-1">
              Trades
            </span>
            <TradesCheckboxGroup
              selected={form.trades}
              onChange={(t) => onFormChange({ ...form, trades: t })}
            />
          </div>

          <div>
            <label htmlFor="vendor-phone" className="block text-sm font-medium text-foreground mb-1">
              Phone
            </label>
            <input
              id="vendor-phone"
              data-testid="vendor-form-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => onFormChange({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              aria-label="Vendor phone"
              placeholder="+1 555 000 0000"
            />
          </div>

          <div>
            <label htmlFor="vendor-email" className="block text-sm font-medium text-foreground mb-1">
              Email
            </label>
            <input
              id="vendor-email"
              data-testid="vendor-form-email"
              type="email"
              value={form.email}
              onChange={(e) => onFormChange({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              aria-label="Vendor email"
              placeholder="vendor@example.com"
            />
          </div>

          <div>
            <label htmlFor="vendor-notes" className="block text-sm font-medium text-foreground mb-1">
              Notes
            </label>
            <textarea
              id="vendor-notes"
              data-testid="vendor-form-notes"
              value={form.notes}
              onChange={(e) => onFormChange({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[80px] resize-y"
              aria-label="Vendor notes"
              placeholder="Additional notes..."
            />
          </div>

          {children}

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex gap-3 justify-end pt-1">
            <Button variant="outline" size="sm" className="min-h-[44px] text-sm" onClick={handleCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              data-testid="vendor-form-submit"
              size="sm"
              className="min-h-[44px] text-sm"
              onClick={onSubmit}
              disabled={submitting || !form.name.trim()}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function SettingsVendorsPage() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioning, setActioning] = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<VendorFormState>(emptyForm);
  const [createError, setCreateError] = useState<string | null>(null);
  const addVendorTriggerRef = useRef<HTMLButtonElement>(null);

  // Edit modal
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [editForm, setEditForm] = useState<VendorFormState>(emptyForm);
  const [editError, setEditError] = useState<string | null>(null);
  const editVendorTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Deactivate confirm
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listVendors();
      setVendors(data.vendors);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  async function handleCreate() {
    if (!createForm.name.trim()) {
      setCreateError('Name is required');
      return;
    }
    setActioning(true);
    setCreateError(null);
    try {
      const body: CreateVendorBody = {
        name: createForm.name.trim(),
        trades: createForm.trades,
        phone: createForm.phone.trim() || undefined,
        email: createForm.email.trim() || undefined,
        notes: createForm.notes.trim() || undefined,
      };
      const newVendor = await createVendor(body);
      setVendors((prev) => [newVendor, ...prev]);
      setShowCreateModal(false);
      setCreateForm(emptyForm());
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create vendor');
    } finally {
      setActioning(false);
    }
  }

  async function handleEdit() {
    if (!editingVendor || !editForm.name.trim()) {
      setEditError('Name is required');
      return;
    }
    setActioning(true);
    setEditError(null);
    try {
      const body: UpdateVendorBody = {
        name: editForm.name.trim(),
        trades: editForm.trades,
        phone: editForm.phone.trim() || null,
        email: editForm.email.trim() || null,
        notes: editForm.notes.trim() || null,
      };
      const updatedVendor = await updateVendor(editingVendor.id, body);
      setVendors((prev) =>
        prev.map((v) => (v.id === editingVendor.id ? updatedVendor : v)),
      );
      setEditingVendor(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update vendor');
    } finally {
      setActioning(false);
    }
  }

  async function handleDeactivate(id: string) {
    setActioning(true);
    try {
      await deactivateVendor(id);
      setVendors((prev) => prev.map((v) => (v.id === id ? { ...v, isActive: false } : v)));
      setDeactivatingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate vendor');
    } finally {
      setActioning(false);
    }
  }

  return (
    <SettingsLayout>
      <div className="max-w-4xl mx-auto pb-6" data-testid="settings-vendors-page">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader
            title="Vendors"
            subtitle="Manage vendors available to the repair agent for dispatch."
            action={
              <Button
                ref={addVendorTriggerRef}
                data-testid="add-vendor-button"
                onClick={() => {
                  setShowCreateModal(true);
                  setCreateForm(emptyForm());
                  setCreateError(null);
                }}
                aria-label="Add new vendor"
              >
                + Add Vendor
              </Button>
            }
          />
        </div>

        {error && (
          <div role="alert" className="mx-[var(--page-padding-x)] mb-4 p-3 bg-destructive/5 border border-destructive/30 rounded-lg text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="px-[var(--page-padding-x)] space-y-2" aria-label="Loading vendors" aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ) : vendors.length === 0 ? (
          <div className="px-[var(--page-padding-x)] py-10 text-center text-muted-foreground">
            <p>No vendors yet. Add one to enable agent dispatching.</p>
          </div>
        ) : (
          <div
            data-testid="vendors-list"
            className="flex flex-col"
            aria-label="Vendors"
          >
            {vendors.map((vendor) => (
              <div
                key={vendor.id}
                data-testid={`vendor-row-${vendor.id}`}
                className="flex flex-col gap-1.5 px-[var(--page-padding-x)] py-3 border-b border-border last:border-b-0"
              >
                {deactivatingId === vendor.id ? (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground flex-1">
                      Deactivate <strong>{vendor.name}</strong>? They will no longer be offered to the agent.
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="min-h-[44px]"
                      onClick={() => setDeactivatingId(null)}
                      disabled={actioning}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="min-h-[44px]"
                      onClick={() => handleDeactivate(vendor.id)}
                      disabled={actioning}
                      data-testid={`vendor-deactivate-confirm-${vendor.id}`}
                    >
                      Deactivate
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{vendor.name}</span>
                        <Badge
                          variant="secondary"
                          className={
                            vendor.isActive
                              ? 'bg-success/15 text-success'
                              : 'bg-muted text-muted-foreground'
                          }
                        >
                          {vendor.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-[44px]"
                          data-testid={`vendor-edit-button-${vendor.id}`}
                          onClick={(e) => {
                            editVendorTriggerRef.current = e.currentTarget as HTMLButtonElement;
                            setEditingVendor(vendor);
                            setEditForm(fromVendor(vendor));
                            setEditError(null);
                          }}
                          aria-label={`Edit vendor ${vendor.name}`}
                        >
                          Edit
                        </Button>
                        {vendor.isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`vendor-deactivate-button-${vendor.id}`}
                            onClick={() => setDeactivatingId(vendor.id)}
                            disabled={actioning}
                            className="min-h-[44px] text-destructive hover:text-destructive"
                            aria-label={`Deactivate vendor ${vendor.name}`}
                          >
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {vendor.trades.length > 0 ? (
                        vendor.trades.map((trade) => (
                          <Badge
                            key={trade}
                            className={CATEGORY_CLASS[trade] ?? 'bg-muted text-foreground'}
                            variant="secondary"
                          >
                            {trade}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">No trades listed</span>
                      )}
                      {vendor.phone && (
                        <span className="text-xs text-muted-foreground">{vendor.phone}</span>
                      )}
                      {vendor.email && (
                        <span className="text-xs text-muted-foreground">{vendor.email}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <VendorModal
            title="Add Vendor"
            form={createForm}
            onFormChange={setCreateForm}
            onSubmit={handleCreate}
            onCancel={() => {
              setShowCreateModal(false);
              setCreateError(null);
            }}
            submitting={actioning}
            error={createError}
            triggerRef={addVendorTriggerRef}
          />
        )}

        {/* Edit Modal */}
        {editingVendor && (
          <VendorModal
            title={`Edit ${editingVendor.name}`}
            form={editForm}
            onFormChange={setEditForm}
            onSubmit={handleEdit}
            onCancel={() => setEditingVendor(null)}
            submitting={actioning}
            error={editError}
            triggerRef={editVendorTriggerRef}
          >
            <CounterpartyAddressesPanel
              owner={{
                kind: 'vendor',
                id: editingVendor.id,
                primaryEmail: editingVendor.email ?? null,
                name: editingVendor.name,
              }}
              onPrimaryChanged={(newPrimary) => {
                setEditForm((prev) => ({ ...prev, email: newPrimary }));
                setVendors((prev) =>
                  prev.map((v) => (v.id === editingVendor.id ? { ...v, email: newPrimary } : v)),
                );
              }}
            />
          </VendorModal>
        )}
      </div>
    </SettingsLayout>
  );
}

export default SettingsVendorsPage;
